import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { scheduleAPI } from '../services/api.ts';
import { toast, ToastContainer } from 'react-toastify';
import { useConfirm } from '../context/ConfirmContext.tsx';
import 'react-toastify/dist/ReactToastify.css';
import MapPicker from '../components/MapPicker.tsx';

// Interface for check-in data (for uploading)
interface CheckInData {
  scheduleId?: string;
  image: File | null;
  latitude: number | null;
  longitude: number | null;
}

// Interface for check-in history items
interface CheckInHistoryItem {
  id: number;
  imageUrl: string;
  latitude: number;
  longitude: number;
  locationName?: string;
  address?: string;
  createdDateTime: string;
  checkOutDateTime?: string | null;
  scheduleId?: number;
}

const CheckIn: React.FC = () => {
  const navigate = useNavigate();
  const [currentLocation, setCurrentLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [locationAccuracy, setLocationAccuracy] = useState<number | null>(null);
  
  // New state variables for the image upload modal
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [checkInData, setCheckInData] = useState<CheckInData>({
    image: null,
    latitude: null,
    longitude: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State variables for check-in history
  const [checkInHistory, setCheckInHistory] = useState<CheckInHistoryItem[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [initialLoadAttempted, setInitialLoadAttempted] = useState(false);
  const MAX_RETRIES = 3;
  const PAGE_SIZE = 10;
  
  // Refs for infinite scroll and pull-to-refresh
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastCheckInItemRef = useRef<HTMLDivElement | null>(null);
  const historyContainerRef = useRef<HTMLDivElement | null>(null);

  // Add a reference to track last API call time
  const lastApiCallTimeRef = useRef<number>(0);
  const MIN_API_CALL_INTERVAL = 2000; // Minimum 2 seconds between API calls

  // Add new state for permission status
  const [permissionStatus, setPermissionStatus] = useState<PermissionState | null>(null);
  const [showPermissionPrompt, setShowPermissionPrompt] = useState(false);

  // Add state for map picker
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');
  const { confirm } = useConfirm();

  // Get current location with improved permission handling
  const getCurrentLocation = () => {
    setLoading(true);
    setLocationError(null);
    setShowPermissionPrompt(false);

    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      setLoading(false);
      return;
    }

    // Check for permissions API support
    if (navigator.permissions && navigator.permissions.query) {
      // Check geolocation permission status
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        setPermissionStatus(result.state);
        
        if (result.state === 'denied') {
          // Permission is denied, show guidance to the user
          setLocationError('Location permission denied. Please enable location services in your device settings.');
          setShowPermissionPrompt(true);
          setLoading(false);
          return;
        }
        
        // For prompt or granted, proceed with geolocation request
        requestGeolocation();
      }).catch(error => {
        console.error('Error checking permission:', error);
        // Fall back to direct geolocation request
        requestGeolocation();
      });
    } else {
      // Permissions API not supported, fall back to direct geolocation request
      requestGeolocation();
    }
  };

  // Helper function to request geolocation
  const requestGeolocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Success callback
        setCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setLocationAccuracy(position.coords.accuracy);
        setLoading(false);
        
        // Update permission status if we successfully got location
        setPermissionStatus('granted');
        setShowPermissionPrompt(false);
        
        // If we're in the check-in modal, update the check-in data
        if (showCheckInModal) {
          setCheckInData(prev => ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }));
        }
      },
      (error) => {
        // Error callback with improved error messages
        console.error('Geolocation error:', error);
        
        let errorMessage;
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access in your device settings.';
            setShowPermissionPrompt(true);
            setPermissionStatus('denied');
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable. Please try again later.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again.';
            break;
          default:
            errorMessage = `Error getting location: ${error.message}`;
        }
        
        setLocationError(errorMessage);
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
  };

  // Get current location on component mount
  useEffect(() => {
    getCurrentLocation();
  }, []);

  // Modify the handleUnscheduledCheckIn function to check for location permission
  const handleUnscheduledCheckIn = () => {
    if (!currentLocation) {
      // If no location, check if we've tried to get it
      if (locationError) {
        // If there was an error, show specific error in toast
        toast.error(locationError);
      } else {
        // If we haven't attempted to get location yet
        toast.info('Please allow location access to check in');
        getCurrentLocation();
      }
      return;
    }

    // Initialize check-in data with current location
    setCheckInData({
      image: null,
      latitude: currentLocation.latitude,
      longitude: currentLocation.longitude,
    });
    
    // Show the check-in modal to upload image
    setShowCheckInModal(true);
  };

  // Handle image selection in the modal
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      // Store the raw File object
      const file = e.target.files[0];
      console.log('Selected file:', file.name, file.type, `${Math.round(file.size / 1024)} KB`);
      setCheckInData(prev => ({
        ...prev,
        image: file
      }));
    }
  };

  // Submit check-in with image
  const handleSubmitCheckIn = async () => {
    if (!checkInData.image) {
      toast.error('Please upload an image');
      return;
    }

    if (!checkInData.latitude || !checkInData.longitude) {
      toast.error('Location data is required. Please enable location services.');
      return;
    }

    setIsSubmitting(true);
    try {
      console.log('Starting direct check-in with image...');
      
      // Call the API with the file as binary data
      await scheduleAPI.checkIn(
        0, // Pass 0 as scheduleId for direct check-in without a schedule
        checkInData.image,
        checkInData.latitude.toString(),
        checkInData.longitude.toString()
      );
      
      // Show success toast and close modal
      toast.success('Successfully checked in!');
      setShowCheckInModal(false);
      
      // Clear the check-in data
      setCheckInData({
        image: null,
        latitude: null,
        longitude: null,
      });
      
      // Refresh the check-in history
      handlePullToRefresh();
      
    } catch (error: any) {
      console.error('Error checking in:', error);
      
      let errorMessage = error.message || 'Failed to check in. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Refresh current location with enhanced error handling
  const refreshLocation = () => {
    // Clear previous state
    setCurrentLocation(null);
    setLocationError(null);
    setLocationAccuracy(null);
    setShowPermissionPrompt(false);
    
    // Show toast to indicate refresh is happening
    toast.info('Refreshing your location...', {
      autoClose: 2000,
      position: 'top-center',
      hideProgressBar: true,
    });
    
    // Check if we're on a mobile device to provide better guidance
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Check for high accuracy in case of mobile devices
    const geoOptions = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0
    };
    
    // If on mobile, check if we have permission before requesting location
    if (isMobileDevice && navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'denied') {
          toast.error('Location permission denied. Please enable it in your device settings.', { 
            autoClose: 5000 
          });
          setLocationError('Location permission denied. Please enable location services in your device settings.');
          setShowPermissionPrompt(true);
          setLoading(false);
          return;
        }
        
        // Proceed with location request if permission is granted or prompt
        getCurrentLocation();
      }).catch(() => {
        // Fall back to direct request if permissions API fails
        getCurrentLocation();
      });
    } else {
      // On desktop or if permissions API is not available, just try to get location
      getCurrentLocation();
    }
  };

  // Function to fetch check-in history
  const fetchCheckInHistory = useCallback(async (page: number, refresh: boolean = false) => {
    if (loadingHistory) {
      console.log("Already loading, skipping request");
      return;
    }
    
    // Rate limiting - prevent calls more frequent than MIN_API_CALL_INTERVAL
    const now = Date.now();
    const timeSinceLastCall = now - lastApiCallTimeRef.current;
    if (timeSinceLastCall < MIN_API_CALL_INTERVAL && !refresh) {
      console.log(`Rate limiting API call. Only ${timeSinceLastCall}ms since last call (minimum ${MIN_API_CALL_INTERVAL}ms)`);
      return;
    }
    
    // Don't retry more than MAX_RETRIES times
    if (retryCount >= MAX_RETRIES && page === historyPage) {
      console.log(`Reached maximum retry attempts (${MAX_RETRIES})`);
      return;
    }
    
    try {
      setLoadingHistory(true);
      setHistoryError(null);
      
      // Update last API call time
      lastApiCallTimeRef.current = now;
      
      console.log(`Fetching check-in history, page ${page}, pageSize ${PAGE_SIZE}`);
      const response = await scheduleAPI.getCheckInHistory(page, PAGE_SIZE);
      console.log("API Response:", response);
      
      // Check if response has a data property that's an array
      if (response && response.data && Array.isArray(response.data)) {
        console.log(`Received ${response.data.length} check-in items`);
        
        // If we got no data and this is not the first page, we've reached the end
        if (response.data.length === 0) {
          console.log("No more data available");
          setHasMoreHistory(false);
          
          // If it's a refresh but there's no data, show a message
          if (refresh && checkInHistory.length === 0) {
            setHistoryError("No check-in history available");
          }
          
          return [];
        }
        
        // Map the API response to our interface structure
        const mappedData = response.data.map((item: any) => {
          console.log("Processing item:", item);
          return {
            id: item.id,
            imageUrl: item.imageUrl || '',
            latitude: item.longandLat ? parseFloat(item.longandLat.split(',')[0]) : 0,
            longitude: item.longandLat ? parseFloat(item.longandLat.split(',')[1]) : 0,
            address: item.address || 'No address',
            createdDateTime: item.createdDateTime,
            checkOutDateTime: item.checkOutDateTime,
            scheduleId: item.scheduleId
          };
        });
        
        if (refresh) {
          setCheckInHistory(mappedData);
        } else {
          setCheckInHistory(prev => [...prev, ...mappedData]);
        }
        
        // If we received fewer items than the page size, we've reached the end
        setHasMoreHistory(response.data.length === PAGE_SIZE);
        setRetryCount(0); // Reset retry count on success
        
        return response.data;
      } else {
        console.error("Invalid response structure:", response);
        throw new Error('Invalid API response format: Expected an object with a data array');
      }
    } catch (error) {
      console.error('Error fetching check-in history:', error);
      setHistoryError('Failed to load check-in history. Please try again.');
      setRetryCount(prev => prev + 1);
      return null;
    } finally {
      setLoadingHistory(false);
      setIsRefreshing(false);
      setInitialLoadAttempted(true);
    }
  }, [loadingHistory, retryCount, historyPage, MAX_RETRIES]);

  // Load initial check-in history
  useEffect(() => {
    let isMounted = true;
    
    if (!initialLoadAttempted && isMounted) {
      console.log("Initial load of check-in history");
      fetchCheckInHistory(1, true)
        .then(() => {
          if (isMounted) {
            console.log("Initial load completed");
          }
        })
        .catch(err => {
          if (isMounted) {
            console.error("Error during initial load:", err);
          }
        });
    }
    
    return () => {
      isMounted = false; // Clean up to prevent state updates on unmounted component
    };
  }, [fetchCheckInHistory, initialLoadAttempted]);

  // Setup intersection observer for infinite scrolling
  useEffect(() => {
    // Don't set up observer if we're loading, there's an error, or there are no more items
    if (loadingHistory || !hasMoreHistory || historyError) {
      console.log("Skipping observer setup:", { loadingHistory, hasMoreHistory, historyError });
      return;
    }
    
    // Only observe if there are items in the history
    if (checkInHistory.length === 0) {
      console.log("No items to observe yet");
      return;
    }
    
    // Disconnect previous observer if exists
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    // Prevent multiple triggers with a timeout
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    
    // Create new observer
    observerRef.current = new IntersectionObserver(entries => {
      const lastItem = entries[0];
      
      if (lastItem.isIntersecting && hasMoreHistory && !loadingHistory) {
        console.log("Last item is visible in viewport, loading more...");
        
        // Debounce the page change to prevent rapid firing
        if (timeoutId) clearTimeout(timeoutId);
        
        timeoutId = setTimeout(() => {
          // Only increment page if not already loading and we have more to load
          if (!loadingHistory && hasMoreHistory) {
            console.log("Triggering next page load:", historyPage + 1);
            setHistoryPage(prev => prev + 1);
          }
        }, 300);
      }
    }, {
      rootMargin: '0px 0px 200px 0px', // Start loading when item is 200px from viewport
      threshold: 0.1 // Trigger when at least 10% of the element is visible
    });
    
    // Observe the last item if it exists
    if (lastCheckInItemRef.current) {
      console.log("Setting up observer on last item");
      observerRef.current.observe(lastCheckInItemRef.current);
    } else {
      console.log("No last item ref to observe");
    }
    
    return () => {
      if (observerRef.current) {
        console.log("Cleaning up observer");
        observerRef.current.disconnect();
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [checkInHistory, loadingHistory, hasMoreHistory, historyError, historyPage]);

  // Fetch more history when page changes
  useEffect(() => {
    if (historyPage > 1 && !loadingHistory && !historyError) {
      console.log(`Loading page ${historyPage} of check-in history`);
      fetchCheckInHistory(historyPage);
    }
  }, [historyPage, fetchCheckInHistory, loadingHistory, historyError]);

  // Handle manual retry
  const handleRetryHistory = () => {
    setHistoryError(null);
    setRetryCount(0);
    if (checkInHistory.length === 0) {
      fetchCheckInHistory(1, true);
    } else {
      fetchCheckInHistory(historyPage);
    }
  };

  // Handle pull to refresh
  const handlePullToRefresh = useCallback(() => {
    if (loadingHistory) return;
    
    setIsRefreshing(true);
    setHistoryPage(1);
    setHistoryError(null);
    setRetryCount(0);
    fetchCheckInHistory(1, true);
  }, [loadingHistory, fetchCheckInHistory]);

  // Add touch event listeners for pull to refresh
  useEffect(() => {
    const historyContainer = historyContainerRef.current;
    if (!historyContainer) return;
    
    let touchStartY = 0;
    const REFRESH_THRESHOLD = 100; // Pixels to pull down to trigger refresh
    
    const onTouchStart = (e: TouchEvent) => {
      if (historyContainer.scrollTop === 0) {
        touchStartY = e.touches[0].clientY;
      }
    };
    
    const onTouchMove = (e: TouchEvent) => {
      if (touchStartY === 0 || isRefreshing || loadingHistory) return;
      
      const touchY = e.touches[0].clientY;
      const touchDiff = touchY - touchStartY;
      
      if (touchDiff > 0 && historyContainer.scrollTop === 0) {
        // Prevent default to stop natural scroll
        e.preventDefault();
        
        // Apply pull effect (optional: show visual feedback)
        if (touchDiff > REFRESH_THRESHOLD) {
          // Visual indicator that refresh will happen
          historyContainer.style.transform = `translateY(${Math.min(touchDiff / 2, 60)}px)`;
        }
      }
    };
    
    const onTouchEnd = (e: TouchEvent) => {
      if (touchStartY === 0 || isRefreshing || loadingHistory) return;
      
      const touchY = e.changedTouches[0].clientY;
      const touchDiff = touchY - touchStartY;
      
      if (touchDiff > REFRESH_THRESHOLD && historyContainer.scrollTop === 0) {
        handlePullToRefresh();
      }
      
      touchStartY = 0;
      historyContainer.style.transform = 'translateY(0)';
    };
    
    historyContainer.addEventListener('touchstart', onTouchStart);
    historyContainer.addEventListener('touchmove', onTouchMove, { passive: false });
    historyContainer.addEventListener('touchend', onTouchEnd);
    
    return () => {
      historyContainer.removeEventListener('touchstart', onTouchStart);
      historyContainer.removeEventListener('touchmove', onTouchMove);
      historyContainer.removeEventListener('touchend', onTouchEnd);
    };
  }, [isRefreshing, loadingHistory, handlePullToRefresh]);

  // Format date from ISO string to readable format
  const formatDateTime = (isoString: string | null) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('en-MY', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  };

  // Add useEffect to monitor geolocation permission changes
  useEffect(() => {
    if (!navigator.permissions || !navigator.permissions.query) {
      return; // Permissions API not supported
    }

    let permissionObserver: any = null;

    navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
      setPermissionStatus(permissionStatus.state);
      
      // Update state when permission changes
      permissionObserver = (event: any) => {
        console.log('Permission state changed:', event.target.state);
        setPermissionStatus(event.target.state);
        
        if (event.target.state === 'granted') {
          // If permission is granted, get location
          getCurrentLocation();
        } else if (event.target.state === 'denied') {
          // If permission is denied, show error
          setLocationError('Location permission denied. Please enable location services in your device settings.');
          setShowPermissionPrompt(true);
        }
      };
      
      // Listen for permission changes
      permissionStatus.addEventListener('change', permissionObserver);
    });

    // Cleanup
    return () => {
      if (permissionObserver) {
        navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
          permissionStatus.removeEventListener('change', permissionObserver);
        });
      }
    };
  }, []);

  // Ensure check-in data is updated with location when location changes
  useEffect(() => {
    if (currentLocation && showCheckInModal) {
      setCheckInData(prev => ({
        ...prev,
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude
      }));
    }
  }, [currentLocation, showCheckInModal]);

  // Function to handle address click
  const handleAddressClick = (address: string) => {
    setSelectedAddress(address);
    setShowMapPicker(true);
  };

  return (
    <div className="container-app py-6">
      <ToastContainer position="top-right" autoClose={3000} />
      <h1 className="text-2xl font-bold mb-4 md:block hidden">Clock-in</h1>

      {/* Check-in Modal */}
      {showCheckInModal && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end items-center bg-black bg-opacity-70 transition-all duration-300 animate-fadeIn pb-safe">
          <div className="w-full bg-white rounded-t-2xl shadow-xl overflow-hidden transform transition-transform duration-300 animate-slideUp mb-16 md:mb-4">
            {/* Header with close button */}
            <div className="relative bg-primary py-4 px-4">
              <h2 className="text-xl font-bold text-white text-center">Check In</h2>
              <button 
                onClick={() => setShowCheckInModal(false)}
                className="absolute right-4 top-4 text-white"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Body content */}
            <div className="p-4">
              {/* Image Upload */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Image <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleImageChange}
                  ref={fileInputRef}
                  className="hidden"
                />
                
                <div 
                  className={`relative border-2 ${checkInData.image ? 'border-primary' : 'border-dashed border-gray-300'} rounded-lg overflow-hidden`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {checkInData.image ? (
                    <div className="relative">
                      <img
                        src={URL.createObjectURL(checkInData.image)}
                        alt="Preview"
                        className="w-full h-48 object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white px-2 py-1 text-xs">
                        {checkInData.image.name} ({Math.round(checkInData.image.size / 1024)} KB)
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCheckInData(prev => ({ ...prev, image: null }));
                        }}
                        className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-md text-red-500"
                        aria-label="Remove image"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-6 px-4 text-center">
                      <svg className="w-10 h-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-600 font-medium">Tap to take a photo</p>
                      <p className="text-xs text-gray-500 mt-1">JPG, PNG, or HEIC</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Location Status */}
              <div className="flex items-center mb-5">
                <div className="mr-2">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm text-gray-700">Location Status</div>
                  {checkInData.latitude && checkInData.longitude ? (
                    <div className="text-sm text-green-600">
                      ✓ Location captured
                    </div>
                  ) : loading ? (
                    <div className="text-sm text-yellow-600 flex items-center">
                      <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-yellow-600 mr-1"></div>
                      Capturing location...
                    </div>
                  ) : locationError ? (
                    <div className="text-sm text-red-600 flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span className="flex-1">Location error</span>
                      <button 
                        onClick={getCurrentLocation} 
                        className="ml-2 text-primary text-xs underline"
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    <div className="text-sm text-yellow-600">
                      Waiting for location...
                    </div>
                  )}
                  
                  {locationError && showPermissionPrompt && (
                    <div className="mt-2 text-xs text-red-600">
                      Please enable location in your device settings
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer with action buttons */}
            <div className="border-t border-gray-200 p-4">
              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={handleSubmitCheckIn}
                  disabled={isSubmitting || !checkInData.image || !checkInData.latitude || !checkInData.longitude}
                  className={`w-full py-3 rounded-md text-white font-medium text-center ${
                    isSubmitting || !checkInData.image || !checkInData.latitude || !checkInData.longitude
                      ? 'bg-gray-400'
                      : 'bg-primary'
                  }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : 'Submit Check-in'}
                </button>
                
                {locationError && (
                  <button
                    onClick={getCurrentLocation}
                    className="w-full py-3 border border-blue-500 rounded-md text-blue-600 font-medium text-center bg-white flex items-center justify-center"
                    disabled={isSubmitting || loading}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Try Get Location Again
                  </button>
                )}
                
                <button
                  onClick={() => setShowCheckInModal(false)}
                  className="w-full py-3 border border-gray-300 rounded-md text-gray-700 font-medium text-center bg-white"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Location Status with Permission Handling */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h2 className="text-lg font-semibold mb-2">Your Location</h2>
        
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            <span className="ml-2 text-gray-600">Getting your location...</span>
          </div>
        ) : locationError ? (
          <div className="bg-red-50 p-4 rounded mb-4">
            <p className="text-red-700">{locationError}</p>
            
            {showPermissionPrompt && (
              <div className="mt-3 mb-2">
                <p className="text-sm text-gray-700 mb-2">To use the check-in feature, we need access to your location:</p>
                <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1 ml-2">
                  <li>Open your device Settings</li>
                  <li>Go to Apps or Application Manager</li>
                  <li>Find your browser or this web app</li>
                  <li>Enable Location permissions</li>
                </ol>
              </div>
            )}
            
            <div className="mt-3 flex space-x-2">
              <button 
                onClick={refreshLocation}
                className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark"
              >
                Try Again
              </button>
              
              {showPermissionPrompt && (
                <a 
                  href="https://support.google.com/chrome/answer/142065?hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                >
                  Help
                </a>
              )}
            </div>
          </div>
        ) : currentLocation ? (
          <div>
            <p className="text-green-600 mb-2">✓ Location successfully detected</p>
            <p className="text-sm text-gray-600">
              {`Latitude: ${currentLocation.latitude.toFixed(6)}, Longitude: ${currentLocation.longitude.toFixed(6)}`}
            </p>
            {locationAccuracy && (
              <p className="text-xs text-gray-500 mt-1">
                Accuracy: ±{locationAccuracy.toFixed(1)} meters
              </p>
            )}
            <button 
              onClick={refreshLocation}
              className="mt-2 text-primary text-sm hover:underline flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh Location
            </button>
          </div>
        ) : (
          <div className="py-2">
            <p className="text-gray-600 mb-3">We need your location to check you in.</p>
            <button 
              onClick={getCurrentLocation}
              className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Allow Location Access
            </button>
          </div>
        )}
      </div>

      {/* Quick Clock-in Section */}
      {currentLocation && (
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold">Quick Clock-in</h2>
            <p className="text-sm text-gray-600">Record your current location</p>
          </div>
          
          <div className="p-4">
            {(() => {
              const latest = checkInHistory && checkInHistory.length > 0 ? checkInHistory[0] : null;
              const hasActiveCheckIn = latest && !latest.checkOutDateTime;
              return (
                <button
                  onClick={async () => {
                    if (hasActiveCheckIn) {
                      const ok = await confirm('Clock out now?');
                      if (!ok) return;
                      scheduleAPI.clockOut()
                        .then(() => {
                          toast.success('Clocked out successfully');
                          handlePullToRefresh();
                        })
                        .catch(() => toast.error('Failed to clock out'));
                      return;
                    }
                    handleUnscheduledCheckIn();
                  }}
                  className={`w-full py-3 px-4 rounded-md flex items-center justify-center ${hasActiveCheckIn ? 'bg-gray-300 text-gray-600' : 'bg-primary text-white'}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Checking In...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {hasActiveCheckIn ? 'Clock-out' : 'Clock-in Here'}
                    </>
                  )}
                </button>
              );
            })()}
          </div>
        </div>
      )}

      {/* Check-in History Section */}
      <div className="bg-white rounded-lg shadow mt-6">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Check-in History</h2>
          <p className="text-sm text-gray-600">Your recent check-ins</p>
        </div>
        
        <div 
          ref={historyContainerRef}
          className="divide-y overflow-y-auto max-h-96 transition-transform"
        >
          {isRefreshing && (
            <div className="flex justify-center items-center py-4 text-primary">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary mr-2"></div>
              <span>Refreshing...</span>
            </div>
          )}
          
          {historyError && (
            <div className="p-6 text-center">
              <p className="text-red-600 mb-2">{historyError}</p>
              <button 
                onClick={handleRetryHistory}
                className="mt-2 bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark"
                disabled={loadingHistory}
              >
                {loadingHistory ? 'Loading...' : 'Try Again'}
              </button>
              <button
                onClick={() => {
                  // Log state info for troubleshooting
                  console.log("Check-in status:", {
                    historyPage,
                    hasMoreHistory,
                    loadingHistory,
                    retryCount,
                    initialLoadAttempted,
                    historyLength: checkInHistory.length
                  });
                }}
                className="ml-2 mt-2 bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
              >
                Debug
              </button>
            </div>
          )}
          
          {!historyError && checkInHistory.length === 0 && !loadingHistory && initialLoadAttempted ? (
            <div className="p-6 text-center">
              <p className="text-gray-600">No check-in history found</p>
              <p className="text-sm text-gray-500 mt-1">Check-ins will appear here</p>
            </div>
          ) : (
            checkInHistory.map((checkIn, index) => (
              <div
                key={checkIn.id}
                ref={index === checkInHistory.length - 1 ? lastCheckInItemRef : null}
                className="p-4 hover:bg-gray-50"
              >
                <div className="flex items-start">
                  <div className="w-16 h-16 bg-gray-200 rounded overflow-hidden mr-3 flex-shrink-0">
                    {checkIn.imageUrl ? (
                      <img 
                        src={checkIn.imageUrl} 
                        alt="Check-in" 
                        className="w-full h-full object-cover" 
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/64?text=Image+Error';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-300">
                        <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm text-gray-500">Check In: {formatDateTime(checkIn.createdDateTime)}</p>
                      {checkIn.checkOutDateTime && (
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                          Checked Out
                        </span>
                      )}
                    </div>
                    
                    {checkIn.address && (
                      <p 
                        className="text-sm text-primary cursor-pointer hover:underline flex items-center mt-1"
                        onClick={() => handleAddressClick(checkIn.address!)}
                      >
                        {checkIn.address}
                        <svg className="w-3 h-3 ml-1" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                      </p>
                    )}
                    
                    {checkIn.checkOutDateTime && (
                      <p className="text-sm text-gray-500 mt-2">
                        <span className="font-medium">Check Out:</span> {formatDateTime(checkIn.checkOutDateTime)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          
          {loadingHistory && !isRefreshing && (
            <div className="flex justify-center items-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary mr-2"></div>
              <span className="text-gray-600">Loading more...</span>
            </div>
          )}
          
          {!hasMoreHistory && checkInHistory.length > 0 && !historyError && (
            <div className="p-4 text-center text-gray-500 text-sm">
              No more check-ins to load
            </div>
          )}
        </div>
      </div>
      
      {/* Map Picker Modal */}
      {showMapPicker && (
        <MapPicker
          isOpen={showMapPicker}
          onClose={() => setShowMapPicker(false)}
          address={selectedAddress}
        />
      )}
    </div>
  );
};

export default CheckIn; 